import * as XLSX from 'xlsx';

/**
 * Generates and downloads the Delivery Performance Excel report.
 * Sheet 1: Entregas (regular deliveries)
 * Sheet 2: Postergados (separate tab)
 * Sheet 3: Resumen (summary)
 *
 * @param {Object}   data             - Response from /api/stats/delivery-performance
 * @param {Array}    postponed        - Response from /api/delivery-points/postponed
 * @param {string}   period
 * @param {string}   customStartDate
 * @param {string}   customEndDate
 */
export function downloadDeliveryPerformanceExcel(data, postponed = [], period, customStartDate, customEndDate) {

  // ── Sheet 1: Entregas ───────────────────────────────────────
  const deliveryHeaders = [
    'N°', 'ID Entrega', 'Usuario', 'Fecha', 'Cliente',
    'Producto', 'Estado', 'Notas Conductor',
    'Monto Venta (S/)', 'Cobrado (S/)', 'FastMax (S/)', 'Cambio (S/)', 'Total Cobrado (S/)'
  ];

  const deliveryRows = (data.deliveries || []).map((d, i) => [
    i + 1,
    d.id,
    d.username || '',
    d.delivery_date || '',
    d.recipient_name || '',
    d.product || '',
    d.status || '',
    d.notes_driver || '',
    d.charge                  ? Number(d.charge).toFixed(2)                  : '0.00',
    d.charged                 ? Number(d.charged).toFixed(2)                 : '0.00',
    d.fastmax_transfer_amount ? Number(d.fastmax_transfer_amount).toFixed(2) : '0.00',
    d.fastmax_exchange_amount ? Number(d.fastmax_exchange_amount).toFixed(2) : '0.00',
    d.total_charged           ? Number(d.total_charged).toFixed(2)           : '0.00',
  ]);

  const ws1 = XLSX.utils.aoa_to_sheet([deliveryHeaders, ...deliveryRows]);
  ws1['!cols'] = [
    { wch: 5 },  { wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 25 },
    { wch: 30 }, { wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 12 },
    { wch: 12 }, { wch: 12 }, { wch: 15 },
  ];

  // ── Sheet 2: Postergados ────────────────────────────────────
  const postponedHeaders = [
    'ID', 'Usuario', 'Fecha Postergación', 'Cliente',
    'Distrito', 'Estado', 'Motivo Postergación'
  ];

  const postponedRows = postponed.map(p => [
    p.id,
    p.username || '',
    p.postponed_from_date || '',
    p.recipient_name || '',
    p.district || '',
    p.status || '',
    p.postponed_reason || '',
  ]);

  const ws2 = XLSX.utils.aoa_to_sheet([postponedHeaders, ...postponedRows]);
  ws2['!cols'] = [
    { wch: 10 }, { wch: 20 }, { wch: 20 }, { wch: 25 },
    { wch: 20 }, { wch: 12 }, { wch: 40 },
  ];

  // ── Sheet 3: Resumen ────────────────────────────────────────
  const summaryData = [
    { 'Métrica': 'RESUMEN GENERAL',      'Valor': '' },
    { 'Métrica': '',                      'Valor': '' },
    { 'Métrica': 'Total Entregas',        'Valor': data.total_deliveries },
    { 'Métrica': 'Total Paquetes',        'Valor': data.total_packages },
    { 'Métrica': 'Tasa de Éxito (%)',     'Valor': data.success_rate },
    { 'Métrica': 'Total Postergados',     'Valor': postponed.length },
    { 'Métrica': '',                      'Valor': '' },
    { 'Métrica': 'DESGLOSE POR ESTADO',  'Valor': '' },
    { 'Métrica': '',                      'Valor': '' },
  ];

  Object.entries(data.status_breakdown || {}).forEach(([status, count]) => {
    summaryData.push({
      'Métrica': `  ${status}`,
      'Valor':   `${count} (${data.status_percentages?.[status] ?? 0}%)`,
    });
  });

  if (data.user_stats && Object.keys(data.user_stats).length > 0) {
    summaryData.push({ 'Métrica': '', 'Valor': '' });
    summaryData.push({ 'Métrica': 'ENTREGAS POR USUARIO', 'Valor': '' });
    summaryData.push({ 'Métrica': '', 'Valor': '' });

    Object.entries(data.user_stats)
      .sort((a, b) => b[1].total - a[1].total)
      .forEach(([username, stats]) => {
        summaryData.push({ 'Métrica': username,         'Valor': `Total: ${stats.total}` });
        summaryData.push({ 'Métrica': '  Entregados',   'Valor': stats.entregado });
        summaryData.push({ 'Métrica': '  Rechazados',   'Valor': stats.rechazado });
        summaryData.push({ 'Métrica': '  Cancelados',   'Valor': stats.cancelado });
        if (stats.pending   > 0) summaryData.push({ 'Métrica': '  Pendientes', 'Valor': stats.pending });
        if (stats.pospuesto > 0) summaryData.push({ 'Métrica': '  Pospuestos', 'Valor': stats.pospuesto });
        if (stats.encamino  > 0) summaryData.push({ 'Métrica': '  En Camino',  'Valor': stats.encamino });
        summaryData.push({ 'Métrica': '', 'Valor': '' });
      });
  }

  const ws3 = XLSX.utils.json_to_sheet(summaryData);
  ws3['!cols'] = [{ wch: 35 }, { wch: 25 }];

  // ── Build workbook ──────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, 'Entregas');
  XLSX.utils.book_append_sheet(wb, ws2, 'Postergados');
  XLSX.utils.book_append_sheet(wb, ws3, 'Resumen');

  const periodLabel = period === 'custom'
    ? `${customStartDate}_${customEndDate}`
    : period;
  XLSX.writeFile(wb, `Rendimiento_Entregas_${periodLabel}_${new Date().toISOString().split('T')[0]}.xlsx`);
}